import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  Clock3Icon,
  Edit3Icon,
  ExternalLinkIcon,
  FlagIcon,
  RefreshCwIcon,
  SaveIcon,
  SearchIcon,
  ShieldAlertIcon,
  XCircleIcon,
} from "lucide-react";

import { supabase } from "../../lib/supabaseClient";

const STATUS_LABELS = {
  pending: "Väntar",
  approved: "Godkänd",
  rejected: "Avslagen",
};

const statusClasses = {
  pending:
    "border-[#dfcfab] bg-[#fbf5e7] text-[#8a6623]",
  approved:
    "border-[#bfd7c7] bg-[#edf6f0] text-[#287148]",
  rejected:
    "border-[#dfc5c0] bg-[#fff4f1] text-[#a0443e]",
};

const INPUT_CLASS =
  "w-full rounded-xl border border-[#c9c1b2] bg-white px-3 py-2.5 text-sm text-[#1f2d26] outline-none transition placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10";

const TABLE_INPUT_CLASS =
  "w-full min-w-[68px] rounded-lg border border-[#cfc7b8] bg-white px-2 py-1.5 text-center text-xs text-[#1f2d26] outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10";

const getFunctionErrorMessage = async (
  error,
  fallback
) => {
  if (!error) return fallback;

  try {
    if (error.context?.json) {
      const payload =
        await error.context.json();

      return (
        payload?.error ||
        payload?.message ||
        fallback
      );
    }
  } catch (parseError) {
    console.warn(
      "Kunde inte läsa Edge Function-felet:",
      parseError
    );
  }

  return (
    error.message ||
    fallback
  );
};

const formatDate = (value) => {
  if (!value) return "–";

  return new Intl.DateTimeFormat(
    "sv-SE",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  ).format(
    new Date(value)
  );
};

const formatNumber = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "–";
  }

  return String(value).replace(
    ".",
    ","
  );
};

const ratingKey = (
  teeId,
  rating
) =>
  [
    teeId,
    rating.rating_category,
    rating.start_hole,
    rating.hole_count,
  ].join("|");

const distanceKey = (
  teeId,
  holeId
) =>
  `${teeId}|${holeId}`;

const getHoleMeta = (
  course,
  holeId
) => {
  for (
    const tee of
      course.tees || []
  ) {
    const handicap =
      (
        tee.handicaps ||
        []
      ).find(
        (item) =>
          item.hole_id ===
          holeId
      );

    if (handicap) {
      return handicap;
    }
  }

  return null;
};

const createEditDraft = (
  course
) => {
  const tees =
    (course.tees || []).map(
      (tee) => ({
        id: tee.id,
        name: tee.name || "",
      })
    );

  const ratings =
    (course.tees || []).flatMap(
      (tee) =>
        (
          tee.ratings ||
          []
        ).map(
          (rating) => ({
            key: ratingKey(
              tee.id,
              rating
            ),
            teeId:
              tee.id,
            ratingCategory:
              rating.rating_category,
            startHole:
              rating.start_hole,
            holeCount:
              rating.hole_count,
            courseRating:
              rating.course_rating ??
              "",
            slopeRating:
              rating.slope_rating ??
              "",
            ratingDate:
              rating.rating_date ||
              "",
          })
        )
    );

  const distances =
    (course.tees || []).flatMap(
      (tee) =>
        (
          tee.distances ||
          []
        ).map(
          (distance) => ({
            key: distanceKey(
              tee.id,
              distance.hole_id
            ),
            teeId:
              tee.id,
            holeId:
              distance.hole_id,
            distanceMeters:
              distance.distance_meters ??
              "",
          })
        )
    );

  const holes =
    (course.holes || []).map(
      (hole) => {
        const meta =
          getHoleMeta(
            course,
            hole.id
          );

        return {
          holeId:
            hole.id,
          holeNumber:
            hole.hole_number,
          par:
            meta?.par ??
            "",
          strokeIndex:
            meta?.stroke_index ??
            "",
        };
      }
    );

  return {
    name:
      course.name || "",
    sourceUrl:
      course.source_url ||
      "",
    sourceNote:
      course.source_note ||
      "",
    tees,
    ratings,
    distances,
    holes,
  };
};

export default function GolfCoursesAdmin({
  onChanged,
}) {
  const [
    courses,
    setCourses,
  ] = useState([]);

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    selectedId,
    setSelectedId,
  ] = useState(null);

  const [
    activeStatus,
    setActiveStatus,
  ] = useState("pending");

  const [
    note,
    setNote,
  ] = useState("");

  const [
    isLoading,
    setIsLoading,
  ] = useState(true);

  const [
    actionId,
    setActionId,
  ] = useState(null);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState("");

  const [
    inlineMessage,
    setInlineMessage,
  ] = useState(null);

  const [
    isEditing,
    setIsEditing,
  ] = useState(false);

  const [
    editDraft,
    setEditDraft,
  ] = useState(null);

  const [
    isSavingEdit,
    setIsSavingEdit,
  ] = useState(false);

  const loadCourses =
    useCallback(
      async () => {
        setIsLoading(true);
        setErrorMessage("");

        try {
          const {
            data,
            error,
          } =
            await supabase.functions.invoke(
              "manage-golf-courses",
              {
                body: {
                  action:
                    "list",
                  status:
                    activeStatus ===
                    "all"
                      ? ""
                      : activeStatus,
                },
              }
            );

          if (error) {
            const message =
              await getFunctionErrorMessage(
                error,
                "Golfbanorna kunde inte hämtas."
              );

            setErrorMessage(
              message
            );
            setCourses([]);
            return;
          }

          if (
            !data?.success
          ) {
            setErrorMessage(
              data?.error ||
                "Golfbanorna kunde inte hämtas."
            );
            setCourses([]);
            return;
          }

          const nextCourses =
            Array.isArray(
              data.courses
            )
              ? data.courses
              : [];

          setCourses(
            nextCourses
          );

          setSelectedId(
            (current) => {
              if (
                current &&
                nextCourses.some(
                  (item) =>
                    item.id ===
                    current
                )
              ) {
                return current;
              }

              return (
                nextCourses[0]
                  ?.id ||
                null
              );
            }
          );
        } catch (error) {
          console.error(
            "Kunde inte läsa golfbanor:",
            error
          );

          setErrorMessage(
            "Golfbanorna kunde inte hämtas."
          );
          setCourses([]);
        } finally {
          setIsLoading(false);
        }
      },
      [activeStatus]
    );

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const selectedCourse =
    useMemo(
      () =>
        courses.find(
          (item) =>
            item.id ===
            selectedId
        ) || null,
      [
        courses,
        selectedId,
      ]
    );

  const filteredCourses =
    useMemo(() => {
      const query = search
        .trim()
        .toLocaleLowerCase(
          "sv-SE"
        );

      if (!query) {
        return courses;
      }

      return courses.filter(
        (course) => {
          const haystack = [
            course.name,
            course.club?.name,
            course.club?.city,
            course.club
              ?.country_code,
            course.creator_email,
          ]
            .filter(Boolean)
            .join(" ")
            .toLocaleLowerCase(
              "sv-SE"
            );

          return haystack.includes(
            query
          );
        }
      );
    }, [
      courses,
      search,
    ]);

  const handleSelectCourse = (
    courseId
  ) => {
    if (
      actionId ||
      isSavingEdit
    ) {
      return;
    }

    if (
      isEditing &&
      !window.confirm(
        "Du har ett öppet redigeringsläge. Lämna banan utan att spara ändringarna?"
      )
    ) {
      return;
    }

    setSelectedId(
      courseId
    );
    setNote("");
    setErrorMessage("");
    setSuccessMessage("");
    setInlineMessage(
      null
    );
    setIsEditing(false);
    setEditDraft(null);
  };

  const changeStatusFilter = (
    status
  ) => {
    if (
      actionId ||
      isSavingEdit
    ) {
      return;
    }

    if (
      isEditing &&
      !window.confirm(
        "Du har ett öppet redigeringsläge. Byt vy utan att spara ändringarna?"
      )
    ) {
      return;
    }

    setActiveStatus(
      status
    );
    setSelectedId(null);
    setNote("");
    setErrorMessage("");
    setSuccessMessage("");
    setInlineMessage(
      null
    );
    setIsEditing(false);
    setEditDraft(null);
  };

  const startEditing = () => {
    if (!selectedCourse) {
      return;
    }

    setEditDraft(
      createEditDraft(
        selectedCourse
      )
    );
    setIsEditing(true);
    setInlineMessage(
      null
    );
    setErrorMessage("");
    setSuccessMessage("");
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditDraft(null);
    setInlineMessage(
      null
    );
  };

  const saveAdminEdit =
    async () => {
      if (
        !selectedCourse ||
        !editDraft
      ) {
        return;
      }

      setIsSavingEdit(true);
      setInlineMessage(
        null
      );
      setErrorMessage("");
      setSuccessMessage("");

      try {
        const {
          data,
          error,
        } =
          await supabase.functions.invoke(
            "manage-golf-courses",
            {
              body: {
                action:
                  "update",
                courseId:
                  selectedCourse.id,
                edit:
                  editDraft,
              },
            }
          );

        if (error) {
          const message =
            await getFunctionErrorMessage(
              error,
              "Ändringarna kunde inte sparas."
            );

          setInlineMessage({
            type: "error",
            text: message,
          });
          return;
        }

        if (
          !data?.success
        ) {
          setInlineMessage({
            type: "error",
            text:
              data?.error ||
              "Ändringarna kunde inte sparas.",
          });
          return;
        }

        if (data.course) {
          setCourses(
            (current) =>
              current.map(
                (course) =>
                  course.id ===
                  data.course.id
                    ? data.course
                    : course
              )
          );
        } else {
          await loadCourses();
        }

        setIsEditing(false);
        setEditDraft(null);

        setInlineMessage({
          type: "success",
          text:
            data.message ||
            "Ändringarna sparades.",
        });
      } catch (error) {
        console.error(
          "Adminredigeringen misslyckades:",
          error
        );

        setInlineMessage({
          type: "error",
          text:
            "Ändringarna kunde inte sparas.",
        });
      } finally {
        setIsSavingEdit(
          false
        );
      }
    };

  const runAction =
    async (action) => {
      if (
        !selectedCourse
      ) {
        return;
      }

      if (isEditing) {
        setInlineMessage({
          type: "error",
          text:
            "Spara eller avbryt redigeringen innan du behandlar banan.",
        });
        return;
      }

      const isApproval =
        action ===
        "approve";

      if (
        !isApproval &&
        note.trim().length <
          5
      ) {
        setInlineMessage({
          type: "error",
          text:
            "Skriv minst 5 tecken i granskningsanteckningen innan du avslår banan.",
        });
        return;
      }

      if (
        isApproval &&
        selectedCourse.validation &&
        !selectedCourse
          .validation.valid
      ) {
        setInlineMessage({
          type: "error",
          text:
            "Banan har valideringsfel och kan inte godkännas ännu. Använd Redigera bana för att rätta uppgifterna.",
        });
        return;
      }

      const confirmed =
        window.confirm(
          isApproval
            ? `Godkänn ${selectedCourse.club?.name || "klubben"} – ${selectedCourse.name} och gör banan tillgänglig i HoleInOne?`
            : `Avslå ${selectedCourse.club?.name || "klubben"} – ${selectedCourse.name}?`
        );

      if (!confirmed) {
        return;
      }

      setActionId(
        selectedCourse.id
      );
      setErrorMessage("");
      setSuccessMessage("");
      setInlineMessage(
        null
      );

      try {
        const {
          data,
          error,
        } =
          await supabase.functions.invoke(
            "manage-golf-courses",
            {
              body: {
                action,
                courseId:
                  selectedCourse.id,
                note:
                  note.trim(),
              },
            }
          );

        if (error) {
          const message =
            await getFunctionErrorMessage(
              error,
              isApproval
                ? "Banan kunde inte godkännas."
                : "Banan kunde inte avslås."
            );

          setInlineMessage({
            type: "error",
            text: message,
          });
          return;
        }

        if (
          !data?.success
        ) {
          setInlineMessage({
            type: "error",
            text:
              data?.error ||
              (isApproval
                ? "Banan kunde inte godkännas."
                : "Banan kunde inte avslås."),
          });
          return;
        }

        const targetStatus =
          isApproval
            ? "approved"
            : "rejected";

        setNote("");

        setInlineMessage({
          type: "success",
          text:
            data.message ||
            (isApproval
              ? "Banan godkändes."
              : "Banan markerades som avslagen."),
        });

        setSuccessMessage(
          data.message ||
            (isApproval
              ? "Banan godkändes."
              : "Banan markerades som avslagen.")
        );

        if (
          activeStatus ===
            targetStatus ||
          activeStatus ===
            "all"
        ) {
          await loadCourses();
        } else {
          setActiveStatus(
            targetStatus
          );
          setSelectedId(
            selectedCourse.id
          );
        }

        await onChanged?.();
      } catch (error) {
        console.error(
          "Golfbaneåtgärden misslyckades:",
          error
        );

        setInlineMessage({
          type: "error",
          text:
            "Åtgärden kunde inte genomföras.",
        });
      } finally {
        setActionId(null);
      }
    };

  return (
    <>
      <div className="border-b border-[#e4ded1] bg-[#fffdf8] px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {[
              [
                "pending",
                "Väntande",
              ],
              [
                "approved",
                "Godkända",
              ],
              [
                "rejected",
                "Avslagna",
              ],
              [
                "all",
                "Alla",
              ],
            ].map(
              ([
                value,
                label,
              ]) => (
                <button
                  key={
                    value
                  }
                  type="button"
                  onClick={() =>
                    changeStatusFilter(
                      value
                    )
                  }
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                    activeStatus ===
                    value
                      ? "border-[#183d2d] bg-[#183d2d] text-white"
                      : "border-[#d3ccbf] bg-white text-[#68716c] hover:border-[#183d2d] hover:text-[#183d2d]"
                  }`}
                >
                  {label}
                </button>
              )
            )}
          </div>

          <button
            type="button"
            onClick={
              loadCourses
            }
            disabled={
              isLoading ||
              actionId ||
              isSavingEdit
            }
            className="inline-flex items-center gap-2 rounded-xl border border-[#cfc7b8] bg-white px-3.5 py-2 text-xs font-semibold text-[#183d2d] transition hover:bg-[#f3efe6] disabled:opacity-50"
          >
            <RefreshCwIcon
              className={`h-4 w-4 ${
                isLoading
                  ? "animate-spin"
                  : ""
              }`}
            />
            Uppdatera
          </button>
        </div>
      </div>

      {(errorMessage ||
        successMessage) && (
        <div className="px-4 pt-4 sm:px-6">
          {errorMessage && (
            <MessageBox
              type="error"
              text={
                errorMessage
              }
            />
          )}

          {successMessage && (
            <MessageBox
              type="success"
              text={
                successMessage
              }
            />
          )}
        </div>
      )}

      <div className="grid min-h-[680px] lg:grid-cols-[0.78fr_1.22fr]">
        <aside className="border-b border-[#e4ded1] bg-[#faf8f2] lg:border-b-0 lg:border-r">
          <div className="border-b border-[#e4ded1] px-4 py-4 sm:px-5">
            <p className="text-sm font-semibold text-[#183d2d]">
              {activeStatus ===
              "pending"
                ? "Banor att granska"
                : "Golfbanor"}
            </p>

            <p className="mt-0.5 text-xs text-[#7b817d]">
              {search.trim()
                ? `${filteredCourses.length} av ${courses.length} resultat`
                : `${courses.length} resultat`}
            </p>

            <div className="relative mt-3">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#969b97]" />

              <input
                type="search"
                value={search}
                onChange={(
                  event
                ) =>
                  setSearch(
                    event.target
                      .value
                  )
                }
                placeholder="Sök klubb, bana, ort eller användare..."
                className="w-full rounded-xl border border-[#d3ccbf] bg-white py-2.5 pl-9 pr-3 text-xs text-[#1f2d26] outline-none transition placeholder:text-[#9ba09d] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
              />
            </div>
          </div>

          <div className="max-h-[680px] overflow-y-auto">
            {isLoading ? (
              <div className="p-6 text-center text-sm text-[#68716c]">
                Hämtar
                golfbanor…
              </div>
            ) : filteredCourses.length ===
              0 ? (
              <div className="p-8 text-center">
                <FlagIcon className="mx-auto h-8 w-8 text-[#a8aaa5]" />

                <p className="mt-3 text-sm font-semibold text-[#183d2d]">
                  {search.trim()
                    ? "Ingen bana matchar sökningen"
                    : "Inga banor här"}
                </p>

                <p className="mt-1 text-xs leading-5 text-[#7b817d]">
                  {search.trim()
                    ? "Prova ett annat klubbnamn, bannamn, ort eller användarkonto."
                    : "Inskickade banor visas här när de får status pending."}
                </p>
              </div>
            ) : (
              filteredCourses.map(
                (course) => {
                  const isSelected =
                    course.id ===
                    selectedId;

                  const status =
                    statusClasses[
                      course
                        .approval_status
                    ] ||
                    statusClasses.pending;

                  return (
                    <button
                      key={
                        course.id
                      }
                      type="button"
                      onClick={() =>
                        handleSelectCourse(
                          course.id
                        )
                      }
                      className={`w-full border-b border-[#e9e4da] px-4 py-4 text-left transition sm:px-5 ${
                        isSelected
                          ? "bg-[#e8f0ea]"
                          : "bg-transparent hover:bg-white"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#183d2d] shadow-sm">
                          <FlagIcon className="h-4 w-4" />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-3">
                            <span className="min-w-0">
                              <span className="block truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-[#9a7735]">
                                {course
                                  .club
                                  ?.name ||
                                  "Golfklubb"}
                              </span>

                              <span className="mt-1 block truncate text-sm font-semibold text-[#183d2d]">
                                {
                                  course.name
                                }
                              </span>
                            </span>

                            <span
                              className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] ${status}`}
                            >
                              {STATUS_LABELS[
                                course
                                  .approval_status
                              ] ||
                                course
                                  .approval_status}
                            </span>
                          </span>

                          <span className="mt-2 flex items-center gap-1.5 text-[10px] text-[#969992]">
                            <Clock3Icon className="h-3 w-3" />
                            {formatDate(
                              course.submitted_at ||
                                course.created_at
                            )}
                          </span>

                          {course.validation &&
                            !course
                              .validation
                              .valid && (
                              <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#a0443e]">
                                <AlertTriangleIcon className="h-3 w-3" />
                                Valideringsfel
                              </span>
                            )}
                        </span>
                      </div>
                    </button>
                  );
                }
              )
            )}
          </div>
        </aside>

        <section className="min-w-0 p-4 sm:p-6 lg:p-8">
          {!selectedCourse ? (
            <div className="flex min-h-[420px] items-center justify-center text-center">
              <div>
                <FlagIcon className="mx-auto h-9 w-9 text-[#b1b3ae]" />

                <p className="mt-3 text-sm font-semibold text-[#183d2d]">
                  Välj en
                  golfbana
                </p>

                <p className="mt-1 text-xs text-[#7b817d]">
                  Scorekort och
                  slopeinformation
                  visas här.
                </p>
              </div>
            </div>
          ) : (
            <CourseReview
              course={
                selectedCourse
              }
              note={
                note
              }
              setNote={
                setNote
              }
              actionId={
                actionId
              }
              runAction={
                runAction
              }
              inlineMessage={
                inlineMessage
              }
              isEditing={
                isEditing
              }
              editDraft={
                editDraft
              }
              setEditDraft={
                setEditDraft
              }
              startEditing={
                startEditing
              }
              cancelEditing={
                cancelEditing
              }
              saveAdminEdit={
                saveAdminEdit
              }
              isSavingEdit={
                isSavingEdit
              }
            />
          )}
        </section>
      </div>
    </>
  );
}

function CourseReview({
  course,
  note,
  setNote,
  actionId,
  runAction,
  inlineMessage,
  isEditing,
  editDraft,
  setEditDraft,
  startEditing,
  cancelEditing,
  saveAdminEdit,
  isSavingEdit,
}) {
  const status =
    statusClasses[
      course.approval_status
    ] ||
    statusClasses.pending;

  const totalPar =
    useMemo(() => {
      const values =
        course.holes
          .map(
            (hole) =>
              getHoleMeta(
                course,
                hole.id
              )?.par
          )
          .filter(
            (value) =>
              Number.isFinite(
                Number(value)
              )
          );

      return values.reduce(
        (
          sum,
          value
        ) =>
          sum +
          Number(value),
        0
      );
    }, [course]);

  const draftHoleMap =
    useMemo(
      () =>
        new Map(
          (
            editDraft?.holes ||
            []
          ).map(
            (hole) => [
              hole.holeId,
              hole,
            ]
          )
        ),
      [editDraft]
    );

  const draftTeeMap =
    useMemo(
      () =>
        new Map(
          (
            editDraft?.tees ||
            []
          ).map(
            (tee) => [
              tee.id,
              tee,
            ]
          )
        ),
      [editDraft]
    );

  const draftRatingMap =
    useMemo(
      () =>
        new Map(
          (
            editDraft?.ratings ||
            []
          ).map(
            (rating) => [
              rating.key,
              rating,
            ]
          )
        ),
      [editDraft]
    );

  const draftDistanceMap =
    useMemo(
      () =>
        new Map(
          (
            editDraft?.distances ||
            []
          ).map(
            (distance) => [
              distance.key,
              distance,
            ]
          )
        ),
      [editDraft]
    );

  const updateDraftField = (
    field,
    value
  ) => {
    setEditDraft(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };

  const updateTeeName = (
    teeId,
    value
  ) => {
    setEditDraft(
      (current) => ({
        ...current,
        tees:
          current.tees.map(
            (tee) =>
              tee.id ===
              teeId
                ? {
                    ...tee,
                    name: value,
                  }
                : tee
          ),
      })
    );
  };

  const updateRating = (
    key,
    field,
    value
  ) => {
    setEditDraft(
      (current) => ({
        ...current,
        ratings:
          current.ratings.map(
            (rating) =>
              rating.key ===
              key
                ? {
                    ...rating,
                    [field]:
                      value,
                  }
                : rating
          ),
      })
    );
  };

  const updateHole = (
    holeId,
    field,
    value
  ) => {
    setEditDraft(
      (current) => ({
        ...current,
        holes:
          current.holes.map(
            (hole) =>
              hole.holeId ===
              holeId
                ? {
                    ...hole,
                    [field]:
                      value,
                  }
                : hole
          ),
      })
    );
  };

  const updateDistance = (
    key,
    value
  ) => {
    setEditDraft(
      (current) => ({
        ...current,
        distances:
          current.distances.map(
            (distance) =>
              distance.key ===
              key
                ? {
                    ...distance,
                    distanceMeters:
                      value,
                  }
                : distance
          ),
      })
    );
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-col gap-4 border-b border-[#e4ded1] pb-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9a7735]">
            {course.club
              ?.name ||
              "Golfklubb"}
          </p>

          {isEditing ? (
            <div className="mt-2 max-w-xl">
              <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8a8f8b]">
                Bana
              </label>

              <input
                value={
                  editDraft?.name ||
                  ""
                }
                onChange={(
                  event
                ) =>
                  updateDraftField(
                    "name",
                    event.target
                      .value
                  )
                }
                className={`${INPUT_CLASS} mt-1`}
              />
            </div>
          ) : (
            <h2 className="mt-2 text-2xl font-semibold text-[#183d2d]">
              {course.name}
            </h2>
          )}

          <p className="mt-2 text-sm text-[#68716c]">
            {[
              course.club
                ?.city,
              course.club
                ?.country_code,
            ]
              .filter(Boolean)
              .join(" · ") ||
              "Ort ej angiven"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`w-fit rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] ${status}`}
          >
            {STATUS_LABELS[
              course
                .approval_status
            ] ||
              course
                .approval_status}
          </span>

          {!isEditing && (
              <button
                type="button"
                onClick={
                  startEditing
                }
                className="inline-flex items-center gap-2 rounded-xl border border-[#cfc7b8] bg-white px-3.5 py-2 text-xs font-semibold text-[#183d2d] transition hover:bg-[#f3efe6]"
              >
                <Edit3Icon className="h-4 w-4" />
                Redigera bana
              </button>
            )}
        </div>
      </div>

      {isEditing && (
        <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#c9d7cd] bg-[#f0f6f2] px-4 py-3 text-sm text-[#315d45]">
          <Edit3Icon className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              Redigeringsläge
            </p>
            <p className="mt-0.5 text-xs leading-5">
              Ändringarna
              sparas i den
              inskickade banan
              först när du
              klickar på Spara
              ändringar.
              Klubbkoppling och
              antal hål är låsta.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-3 py-6 sm:grid-cols-2 lg:grid-cols-4">
        <InfoCard
          label="Hål"
          value={`${course.hole_count}`}
        />

        <InfoCard
          label="Par"
          value={
            totalPar ||
            "–"
          }
        />

        <InfoCard
          label="Tees"
          value={`${course.tees?.length || 0}`}
        />

        <InfoCard
          label="Inskickad av"
          value={
            course.creator_email ||
            "Okänd användare"
          }
        />
      </div>

      <ValidationBox
        validation={
          course.validation
        }
        isEditing={
          isEditing
        }
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-[#e0d9cc] bg-[#faf8f2] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
            Källa
          </p>

          {isEditing ? (
            <div className="mt-3 space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8a8f8b]">
                  Källänk
                </label>

                <input
                  type="url"
                  value={
                    editDraft?.sourceUrl ||
                    ""
                  }
                  onChange={(
                    event
                  ) =>
                    updateDraftField(
                      "sourceUrl",
                      event.target
                        .value
                    )
                  }
                  placeholder="https://..."
                  className={`${INPUT_CLASS} mt-1`}
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8a8f8b]">
                  Källanteckning
                </label>

                <textarea
                  rows={4}
                  value={
                    editDraft?.sourceNote ||
                    ""
                  }
                  onChange={(
                    event
                  ) =>
                    updateDraftField(
                      "sourceNote",
                      event.target
                        .value
                    )
                  }
                  className={`${INPUT_CLASS} mt-1 resize-y`}
                />
              </div>
            </div>
          ) : (
            <>
              {course.source_url ? (
                <a
                  href={
                    course.source_url
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-flex items-center gap-2 break-all text-sm font-semibold text-[#183d2d] hover:underline"
                >
                  Öppna angiven
                  källa
                  <ExternalLinkIcon className="h-4 w-4 shrink-0" />
                </a>
              ) : (
                <p className="mt-2 text-sm text-[#68716c]">
                  Ingen källänk
                  angiven.
                </p>
              )}

              {course.source_note && (
                <p className="mt-3 whitespace-pre-wrap text-xs leading-5 text-[#68716c]">
                  {
                    course.source_note
                  }
                </p>
              )}
            </>
          )}
        </div>

        <div className="rounded-2xl border border-[#e0d9cc] bg-[#faf8f2] p-4">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
            Klubb
          </p>

          <p className="mt-2 text-sm font-semibold text-[#183d2d]">
            {course.club
              ?.name ||
              "–"}
          </p>

          <p className="mt-1 text-xs text-[#68716c]">
            Status:{" "}
            {STATUS_LABELS[
              course.club
                ?.approval_status
            ] ||
              course.club
                ?.approval_status ||
              "–"}
          </p>

          {course.club
            ?.website && (
            <a
              href={
                course.club
                  .website
              }
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#183d2d] hover:underline"
            >
              Klubbens
              webbplats
              <ExternalLinkIcon className="h-3.5 w-3.5" />
            </a>
          )}

          {isEditing && (
            <p className="mt-3 rounded-xl border border-[#e2dacb] bg-white px-3 py-2 text-[11px] leading-5 text-[#7b817d]">
              Klubbuppgifter
              redigeras inte
              från en enskild
              bana eftersom
              samma klubb kan
              vara kopplad till
              flera banor.
            </p>
          )}
        </div>
      </div>

      {isEditing && (
        <div className="mt-7">
          <SectionTitle
            title="Tee-namn"
            description="Du kan rätta namnet på befintliga tees. Lägg till eller ta bort tees görs inte i detta adminläge."
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {course.tees.map(
              (tee) => (
                <div
                  key={
                    tee.id
                  }
                  className="rounded-2xl border border-[#e0d9cc] bg-[#faf8f2] p-4"
                >
                  <label className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8a8f8b]">
                    Tee
                  </label>

                  <input
                    value={
                      draftTeeMap.get(
                        tee.id
                      )?.name ||
                      ""
                    }
                    onChange={(
                      event
                    ) =>
                      updateTeeName(
                        tee.id,
                        event.target
                          .value
                      )
                    }
                    className={`${INPUT_CLASS} mt-1`}
                  />
                </div>
              )
            )}
          </div>
        </div>
      )}

      <div className="mt-7">
        <SectionTitle
          title="Course Rating & Slope"
          description={
            isEditing
              ? "Redigera värden och ange ratingdatum om det är känt."
              : "Kontrollera värderingen per tee och kategori."
          }
        />

        <div className="overflow-hidden rounded-2xl border border-[#d8d2c4] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-xs">
              <thead className="bg-[#183d2d] text-white">
                <tr>
                  <th className="px-4 py-3 font-semibold">
                    Tee
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Kategori
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    CR
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Slope
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Hål
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    Datum
                  </th>
                </tr>
              </thead>

              <tbody>
                {course.tees.flatMap(
                  (tee) =>
                    (
                      tee.ratings ||
                      []
                    ).map(
                      (rating) => {
                        const key =
                          ratingKey(
                            tee.id,
                            rating
                          );

                        const draftRating =
                          draftRatingMap.get(
                            key
                          );

                        return (
                          <tr
                            key={
                              key
                            }
                            className="border-t border-[#eee9dc]"
                          >
                            <td className="px-4 py-3 font-semibold text-[#183d2d]">
                              {draftTeeMap.get(
                                tee.id
                              )?.name ||
                                tee.name}
                            </td>

                            <td className="px-4 py-3">
                              {rating.rating_category ===
                              "men"
                                ? "Herr"
                                : "Dam"}
                            </td>

                            <td className="px-4 py-3">
                              {isEditing ? (
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  max="99.9"
                                  value={
                                    draftRating?.courseRating ??
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateRating(
                                      key,
                                      "courseRating",
                                      event.target
                                        .value
                                    )
                                  }
                                  className={TABLE_INPUT_CLASS}
                                />
                              ) : (
                                formatNumber(
                                  rating.course_rating
                                )
                              )}
                            </td>

                            <td className="px-4 py-3">
                              {isEditing ? (
                                <input
                                  type="number"
                                  min="55"
                                  max="155"
                                  step="1"
                                  value={
                                    draftRating?.slopeRating ??
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateRating(
                                      key,
                                      "slopeRating",
                                      event.target
                                        .value
                                    )
                                  }
                                  className={TABLE_INPUT_CLASS}
                                />
                              ) : (
                                formatNumber(
                                  rating.slope_rating
                                )
                              )}
                            </td>

                            <td className="px-4 py-3">
                              {
                                rating.start_hole
                              }
                              –
                              {Number(
                                rating.start_hole
                              ) +
                                Number(
                                  rating.hole_count
                                ) -
                                1}
                            </td>

                            <td className="px-4 py-3">
                              {isEditing ? (
                                <input
                                  type="date"
                                  value={
                                    draftRating?.ratingDate ||
                                    ""
                                  }
                                  onChange={(
                                    event
                                  ) =>
                                    updateRating(
                                      key,
                                      "ratingDate",
                                      event.target
                                        .value
                                    )
                                  }
                                  className="rounded-lg border border-[#cfc7b8] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#183d2d]"
                                />
                              ) : (
                                rating.rating_date ||
                                "–"
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )
                )}

                {course.tees.every(
                  (tee) =>
                    (
                      tee.ratings ||
                      []
                    ).length ===
                    0
                ) && (
                  <tr>
                    <td
                      colSpan={
                        6
                      }
                      className="px-4 py-6 text-center text-[#68716c]"
                    >
                      Ingen
                      slopeinformation
                      hittades.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-7">
        <SectionTitle
          title="Scorekort"
          description={
            isEditing
              ? "Redigera par, SI och meter. Par/SI uppdateras på alla befintliga tee- och ratingrader för hålet."
              : "Par, hålindex och längd från varje registrerad tee."
          }
        />

        <div className="overflow-hidden rounded-2xl border border-[#d8d2c4] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-center text-xs">
              <thead className="bg-[#183d2d] text-white">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">
                    Hål
                  </th>
                  <th className="px-3 py-3 font-semibold">
                    Par
                  </th>
                  <th className="px-3 py-3 font-semibold">
                    SI
                  </th>

                  {course.tees.map(
                    (tee) => (
                      <th
                        key={
                          tee.id
                        }
                        className="px-3 py-3 font-semibold"
                      >
                        {draftTeeMap.get(
                          tee.id
                        )?.name ||
                          tee.name}
                      </th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {course.holes.map(
                  (hole) => {
                    const meta =
                      getHoleMeta(
                        course,
                        hole.id
                      );

                    const draftHole =
                      draftHoleMap.get(
                        hole.id
                      );

                    return (
                      <tr
                        key={
                          hole.id
                        }
                        className="border-t border-[#eee9dc]"
                      >
                        <td className="px-3 py-3 text-left font-semibold text-[#183d2d]">
                          {
                            hole.hole_number
                          }
                        </td>

                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              min="2"
                              max="7"
                              step="1"
                              value={
                                draftHole?.par ??
                                ""
                              }
                              onChange={(
                                event
                              ) =>
                                updateHole(
                                  hole.id,
                                  "par",
                                  event.target
                                    .value
                                )
                              }
                              className={TABLE_INPUT_CLASS}
                            />
                          ) : (
                            meta?.par ??
                            "–"
                          )}
                        </td>

                        <td className="px-3 py-3">
                          {isEditing ? (
                            <input
                              type="number"
                              min="1"
                              max="18"
                              step="1"
                              value={
                                draftHole?.strokeIndex ??
                                ""
                              }
                              onChange={(
                                event
                              ) =>
                                updateHole(
                                  hole.id,
                                  "strokeIndex",
                                  event.target
                                    .value
                                )
                              }
                              className={TABLE_INPUT_CLASS}
                            />
                          ) : (
                            meta?.stroke_index ??
                            "–"
                          )}
                        </td>

                        {course.tees.map(
                          (tee) => {
                            const distance =
                              (
                                tee.distances ||
                                []
                              ).find(
                                (item) =>
                                  item.hole_id ===
                                  hole.id
                              );

                            const key =
                              distanceKey(
                                tee.id,
                                hole.id
                              );

                            const draftDistance =
                              draftDistanceMap.get(
                                key
                              );

                            return (
                              <td
                                key={
                                  tee.id
                                }
                                className="px-3 py-3"
                              >
                                {isEditing ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <input
                                      type="number"
                                      min="1"
                                      max="1000"
                                      step="1"
                                      value={
                                        draftDistance?.distanceMeters ??
                                        ""
                                      }
                                      onChange={(
                                        event
                                      ) =>
                                        updateDistance(
                                          key,
                                          event.target
                                            .value
                                        )
                                      }
                                      className={TABLE_INPUT_CLASS}
                                    />
                                    <span className="text-[10px] text-[#8a8f8b]">
                                      m
                                    </span>
                                  </div>
                                ) : distance?.distance_meters ? (
                                  `${distance.distance_meters} m`
                                ) : (
                                  "–"
                                )}
                              </td>
                            );
                          }
                        )}
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isEditing ? (
        <div className="mt-7 border-t border-[#e4ded1] pt-5">
          {inlineMessage && (
            <div className="mb-4">
              <MessageBox
                type={
                  inlineMessage.type
                }
                text={
                  inlineMessage.text
                }
              />
            </div>
          )}

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={
                saveAdminEdit
              }
              disabled={
                isSavingEdit
              }
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:opacity-50"
            >
              <SaveIcon className="h-4 w-4" />
              {isSavingEdit
                ? "Sparar…"
                : "Spara ändringar"}
            </button>

            <button
              type="button"
              onClick={
                cancelEditing
              }
              disabled={
                isSavingEdit
              }
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#cfc7b8] bg-white px-5 py-3 text-sm font-semibold text-[#183d2d] transition hover:bg-[#f3efe6] disabled:opacity-50"
            >
              <XCircleIcon className="h-4 w-4" />
              Avbryt
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-7">
            <label
              htmlFor="course-admin-note"
              className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]"
            >
              Granskningsanteckning
            </label>

            <textarea
              id="course-admin-note"
              rows={3}
              value={
                note
              }
              onChange={(
                event
              ) =>
                setNote(
                  event.target
                    .value
                )
              }
              placeholder={
                course.approval_status ===
                "pending"
                  ? "Valfritt vid godkännande. Vid avslag krävs minst 5 tecken eftersom användaren behöver veta vad som ska korrigeras."
                  : "Banan är redan behandlad."
              }
              disabled={
                course.approval_status !==
                "pending"
              }
              className="mt-2 w-full resize-y rounded-xl border border-[#c9c1b2] bg-white px-3 py-3 text-sm leading-6 outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10 disabled:bg-[#f3f1eb] disabled:text-[#7b817d]"
            />

            {course.approval_status ===
              "pending" && (
              <p
                className={`mt-1.5 text-[11px] ${
                  note.trim()
                    .length <
                  5
                    ? "text-[#a0443e]"
                    : "text-[#68716c]"
                }`}
              >
                {note.trim()
                  .length <
                5
                  ? `Avslag kräver minst 5 tecken. ${Math.max(
                      0,
                      5 -
                        note.trim()
                          .length
                    )} återstår.`
                  : "Anteckningen är tillräckligt lång för avslag."}
              </p>
            )}
          </div>

          <div className="mt-5 border-t border-[#e4ded1] pt-5">
            {inlineMessage && (
              <div className="mb-4">
                <MessageBox
                  type={
                    inlineMessage.type
                  }
                  text={
                    inlineMessage.text
                  }
                />
              </div>
            )}

            {course.approval_status ===
            "pending" ? (
              <div className="flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() =>
                    runAction(
                      "approve"
                    )
                  }
                  disabled={
                    actionId ===
                      course.id ||
                    (course.validation &&
                      !course
                        .validation
                        .valid)
                  }
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <CheckCircle2Icon className="h-4 w-4" />

                  {actionId ===
                  course.id
                    ? "Behandlar…"
                    : "Godkänn golfbana"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    runAction(
                      "reject"
                    )
                  }
                  disabled={
                    actionId ===
                    course.id
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#d8bdb7] bg-white px-5 py-3 text-sm font-semibold text-[#a0443e] transition hover:bg-[#fff5f3] disabled:opacity-50"
                >
                  <XCircleIcon className="h-4 w-4" />
                  Avslå
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-[#e0d9cc] bg-[#f8f5ed] px-4 py-3 text-xs leading-5 text-[#68716c]">
                Banan
                behandlades{" "}
                {formatDate(
                  course.reviewed_at
                )}
                .

                {course.review_note && (
                  <span className="mt-1 block">
                    Anteckning:{" "}
                    {
                      course.review_note
                    }
                  </span>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ValidationBox({
  validation,
  isEditing,
}) {
  if (!validation) {
    return null;
  }

  return (
    <div className="space-y-3">
      {isEditing && (
        <p className="text-[11px] leading-5 text-[#7b817d]">
          Datakontrollen
          nedan gäller senast
          sparade version. Den
          körs om när du sparar
          adminändringarna.
        </p>
      )}

      {validation.valid ? (
        <div className="flex items-start gap-3 rounded-2xl border border-[#bfd7c7] bg-[#edf6f0] px-4 py-3 text-sm text-[#287148]">
          <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" />

          <div>
            <p className="font-semibold">
              Datakontrollen är
              godkänd
            </p>

            <p className="mt-0.5 text-xs leading-5">
              Hål, tees,
              längder och
              slopeinformation
              är kompletta enligt
              HoleInOnes
              nuvarande krav.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#dfc5c0] bg-[#fff4f1] px-4 py-3 text-sm text-[#a0443e]">
          <div className="flex items-start gap-3">
            <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />

            <div>
              <p className="font-semibold">
                Banan kan inte
                godkännas ännu
              </p>

              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-5">
                {(
                  validation.issues ||
                  []
                ).map(
                  (issue) => (
                    <li
                      key={
                        issue
                      }
                    >
                      {
                        issue
                      }
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {(validation.warnings ||
        []).length >
        0 && (
        <div className="rounded-2xl border border-[#dfcfab] bg-[#fbf5e7] px-4 py-3 text-xs leading-5 text-[#8a6623]">
          <p className="font-semibold">
            Kontrollera även
          </p>

          <ul className="mt-1 list-disc space-y-1 pl-4">
            {validation.warnings.map(
              (
                warning
              ) => (
                <li
                  key={
                    warning
                  }
                >
                  {
                    warning
                  }
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function MessageBox({
  type,
  text,
}) {
  const isSuccess =
    type ===
    "success";

  return (
    <div
      className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? "border-[#bfd7c7] bg-[#edf6f0] text-[#287148]"
          : "border-[#dfc5c0] bg-[#fff8f6] text-[#a0443e]"
      }`}
    >
      {isSuccess ? (
        <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" />
      ) : (
        <ShieldAlertIcon className="mt-0.5 h-4 w-4 shrink-0" />
      )}

      <p>{text}</p>
    </div>
  );
}

function InfoCard({
  label,
  value,
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-[#e0d9cc] bg-[#faf8f2] p-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#8a8f8b]">
        {label}
      </p>

      <p className="mt-2 truncate text-sm font-semibold text-[#183d2d]">
        {value}
      </p>
    </div>
  );
}

function SectionTitle({
  title,
  description,
}) {
  return (
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-[#183d2d]">
        {title}
      </h3>

      <p className="mt-0.5 text-xs leading-5 text-[#7b817d]">
        {description}
      </p>
    </div>
  );
}